import { useState, useEffect } from 'react';

/**
 * 自定义Hook，用于检测iOS设备上的键盘高度
 * @returns {Object} 包含键盘高度和键盘是否可见的对象
 */
export const useKeyboardHeight = () => {
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // 检测是否为iOS设备
    const checkIsIOS = () => {
      // @ts-expect-error MSStream不存在于Window类型定义中
      const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      setIsIOS(isIOSDevice);
      
      // 如果是iOS设备，添加视口meta标签以防止缩放问题
      if (isIOSDevice) {
        const metaViewport = document.querySelector('meta[name="viewport"]');
        if (metaViewport) {
          metaViewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
        }
        
        // 添加iOS设备类名到body
        document.body.classList.add('ios-device');
      }
    };
    
    checkIsIOS();
    
    // 只在iOS设备上添加键盘事件监听
    if (!isIOS) return;
    
    // 监听输入框聚焦事件
    const handleFocus = (e: FocusEvent) => {
      if (!(e.target instanceof HTMLInputElement || 
            e.target instanceof HTMLTextAreaElement || 
            e.target instanceof HTMLSelectElement)) {
        return;
      }
      
      // 延迟检测键盘高度，等待键盘完全显示
      setTimeout(() => {
        // 计算键盘高度：视口高度与可见视口高度的差值
        const keyboardHeight = window.innerHeight - window.visualViewport!.height;
        
        if (keyboardHeight > 0) {
          // 设置键盘高度CSS变量
          document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
          setKeyboardHeight(keyboardHeight);
          setIsKeyboardVisible(true);
          
          // 添加键盘可见类名
          document.body.classList.add('keyboard-visible');
          
          // 滚动到输入框位置
          const inputElement = e.target as HTMLElement;
          const inputRect = inputElement.getBoundingClientRect();
          const inputBottom = inputRect.bottom;
          const visibleHeight = window.visualViewport!.height;
          
          if (inputBottom > visibleHeight) {
            inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 300);
    };
    
    // 监听输入框失焦事件
    const handleBlur = () => {
      // 重置键盘高度
      document.documentElement.style.setProperty('--keyboard-height', '0px');
      setKeyboardHeight(0);
      setIsKeyboardVisible(false);
      
      // 移除键盘可见类名
      document.body.classList.remove('keyboard-visible');
    };
    
    // 监听visualViewport大小变化
    const handleVisualViewportResize = () => {
      if (!window.visualViewport) return;
      
      const keyboardHeight = window.innerHeight - window.visualViewport.height;
      
      if (keyboardHeight > 0) {
        document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
        setKeyboardHeight(keyboardHeight);
        setIsKeyboardVisible(true);
        document.body.classList.add('keyboard-visible');
      } else {
        document.documentElement.style.setProperty('--keyboard-height', '0px');
        setKeyboardHeight(0);
        setIsKeyboardVisible(false);
        document.body.classList.remove('keyboard-visible');
      }
    };
    
    // 添加事件监听
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleBlur, true);
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
    }
    
    // 清理事件监听
    return () => {
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('blur', handleBlur, true);
      
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
      }
    };
  }, [isIOS]);
  
  return { keyboardHeight, isKeyboardVisible, isIOS };
};

export default useKeyboardHeight; 